import TableComponent from "../ui/Table";
import { urls } from "../config/variables";
import { useState, useEffect, useContext, useMemo } from "react";
import useAxios from "../../hooks/useAxios";
import AuthContext from "../../contexts/auth-context";
import AppContext from "../../contexts/app-context";
import {
  Box,
  TextField,
  Pagination,
  Alert,
  CircularProgress,
  InputAdornment,
  Typography,
} from "@mui/material";
import { ArrowDropUp, ArrowDropDown, Search as SearchIcon } from "@mui/icons-material";
import DangerousIcon from "@mui/icons-material/Dangerous";
import InfoIcon from "@mui/icons-material/Info";

const CptIssuesTable = () => {
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const globalFilterQuery = appCtx?.globalFilterQuery || "";
  const token = authCtx.token;
  const { operation } = useAxios();

  const [tableData, setTableData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [tableDataError, setTableDataError] = useState(undefined);

  const GetTableData = () => {
    setLoadingData(true);
    setTableDataError(undefined);

    const globalParams = new URLSearchParams(globalFilterQuery);
    const foundFromDate = globalParams.get("from_date");
    const foundToDate = globalParams.get("to_date");
    const tableParams = new URLSearchParams();

    if (foundFromDate) tableParams.set("from_date", foundFromDate);
    if (foundToDate) tableParams.set("to_date", foundToDate);

    operation(
      {
        method: "GET",
        url: urls.CPT_ISSUES_TABLE + "?" + tableParams,
        headers: { Authorization: `token ${token}` },
      },
      handleSetTableData,
      handleSetTableDataError
    );
  };

  const handleSetTableData = (response) => {
    setLoadingData(false);

    // Reformat the data for consistent structure
    const normalizedData =
      response?.map((item) => {
        const validIssues = item.issue_details?.filter((issue) => issue.count > 0) || [];
        const totalIssues = validIssues.reduce((sum, issue) => sum + issue.count, 0);

        return {
          cpt_code: item.cpt_code || "",
          issue_cost: item.issue_cost || "",
          total_issues: totalIssues,
          issue_details: validIssues.map((issue) => ({
            label: issue.label,
            count: issue.count,
          })),
        };
      }) || [];

    setTableData(normalizedData);
  };

  const handleSetTableDataError = (error) => {
    setLoadingData(false);
    setTableDataError(error);
  };

  useEffect(() => {
    GetTableData();
    // eslint-disable-next-line
  }, []);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;
  const handleChangeCurrentPage = (e, v) => setCurrentPage(v);

  // Search + Sorting
  const [searchValue, setSearchValue] = useState("");
  const [sortBy, setSortBy] = useState("cpt_code");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(columnKey);
      setSortDirection("asc");
    }
  };

  // Filter + Sort
  const filteredData = useMemo(() => {
    let filtered = tableData.filter((item) => {
      const search = searchValue.toLowerCase();

      // Convert issue_details into a searchable string (like "1 Diagnosis not related, 2 Therapeutic duplication")
      const detailsString = item.issue_details
        ?.map((d) => `${d.count} ${d.label}`)
        .join(" ")
        .toLowerCase();

        // Check if any field matches search value
      return (
        item.cpt_code?.toLowerCase().includes(search) ||
        item.issue_cost?.toLowerCase().includes(search) ||
        item.total_issues?.toString().includes(search) ||
        detailsString?.includes(search)
      );
    });

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // handle issue_details sorting by total count or first label alphabetically
      if (sortBy === "issue_details") {
        const aString = a.issue_details.map((d) => d.label).join(", ");
        const bString = b.issue_details.map((d) => d.label).join(", ");
        return sortDirection === "asc"
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      }

      // numeric sort
      if (!isNaN(aVal) && !isNaN(bVal)) {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      // string sort
      if (aVal && bVal) {
        return sortDirection === "asc"
          ? aVal.toString().localeCompare(bVal.toString())
          : bVal.toString().localeCompare(aVal.toString());
      }

      return 0;
    });

    return filtered;
  }, [tableData, searchValue, sortBy, sortDirection]);

  
  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  // Table Headers 
  const tableHeaders = [
    { label: "CPT Code", key: "cpt_code" },
    { label: "Cost", key: "issue_cost" },

    { label: "Total Issues", key: "total_issues" },
    { label: "Details", key: "issue_details" },
  ];

  return (
    <Box sx={{ position: "relative", height: "380px" }}>
      {/* Search */}
      <Box
        sx={{
          alignItems: "center",
          marginBottom: "10px",
          justifyContent: "space-between",
          display: 'flex',
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setCurrentPage(1);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Pagination */}
        {tableData.length > 5 && (
          <Box sx={{ marginRight: "10px" }}>
            <Pagination
              page={currentPage}
              count={totalPages}
              onChange={handleChangeCurrentPage}
              color="primary"
            />
          </Box>
        )}
      </Box>

      {/* Conditional Rendering */}
      {!tableDataError && tableData.length === 0 && !loadingData ? (
        <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
          No data available.
        </Alert>
      ) : tableDataError ? (
        <Alert icon={<DangerousIcon fontSize="inherit" />} severity="error">
          Something went wrong.
        </Alert>
      ) : loadingData ? (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Table */}
          <TableComponent
            tableHead={tableHeaders.map((h) => (
              <Box
                key={h.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => handleSort(h.key)}
              >
                {h.label}
                {sortBy === h.key &&
                  (sortDirection === "asc" ? (
                    <ArrowDropUp fontSize="small" />
                  ) : (
                    <ArrowDropDown fontSize="small" />
                  ))}
              </Box>
            ))}
            tableBody={currentRows.map((row) => {
              const validIssues = row.issue_details?.filter((issue) => issue.count > 0) || [];
              const totalIssues = validIssues.reduce((sum, issue) => sum + issue.count, 0);

              return {
                cpt_code: row.cpt_code,
                issue_cost: row.issue_cost,
                total_issues: `${totalIssues}`,
                issue_details: (
                  <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Box>
                            <ul>
                                {validIssues.map((issue, index) => (
                                    <li key={index} style={{marginBottom: '5px'}}>
                                        <Typography
                                            key={issue.label}
                                            sx={{
                                                textAlign: "center",
                                                width: "fit-content",
                                                fontWeight: "500",
                                            }}
                                        >
                                            {`${issue.count} ${issue.label}`}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                    </Box>
                  </Box>
                ),
              };
            })}
            bodyCells={["cpt_code", "issue_cost", "total_issues", "issue_details"]}
            bodyCellsCustomSymbol={{ issue_cost: '$' }}
            customStyle={{
              tableContainer: {
                maxHeight: '330px',
                overflowY: "auto"
              },
              headerTableCell: (cell) => {
                return ({
                  textAlign: "center",
                  border: '1px solid #d9d9d9',
                  fontSize: "18px",
                  fontWeight: "500",
                  color: "#000000c7",
                  backgroundColor: "#F5F5F5",
                  padding: "10px 0 5px",
                  margin: '100px',
                  width:
                    cell.key === 'issue_cost' ? '15%' :
                      cell.key === 'total_issues' ? '15%' :
                      cell.key === 'cpt_code' ? '15%' :
                        '50%',
                })
              },
              row: {
                  border: '1px solid #d9d9d9',

                transition: "0.2s",
                backgroundColor: "#FCFEFE",
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                },
              },
              tableCell: {
                textAlign: "center",
                border: '1px solid #d9d9d9',
                padding: "20px",
                fontWeight: "500",
                verticalAlign: "middle",
              },
            }}
          />
        </>
      )}
    </Box>
  );
};

export default CptIssuesTable;
